import React, { FC, useState } from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import Avatar from '@adsk/alloy-react-avatar';
import { BasicButton } from '@adsk/alloy-react-button';
import { TriangleUpIcon, TriangleDownIcon } from '@adsk/alloy-react-icon';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { Flyout } from '@adsk/alloy-react-tooltip';
import SubscriptionsModal from '@adsk/alloy-subscriptions-modal';

import Profile from './Profile';
import ProfileLoader from './ProfileLoader';
import { ANALYTICS_TYPES } from '../helpers/analytics';
import { UserType } from '../helpers/types';

type ProfileButtonProps = {
  showSubscriptionsModal?: boolean;
  user?: UserType | null;
  projectId?: string;
  onLogout?: () => void;
  UNSAFE_showBuildersUserProfileModal?: () => void;
  UNSAFE_isBuildersProfileModalEnabled?: boolean;
};

/**
 * Acs ProfileButton
 */
const ProfileButton: FC<ProfileButtonProps> = ({
  showSubscriptionsModal,
  user,
  projectId,
  onLogout,
  UNSAFE_showBuildersUserProfileModal,
  UNSAFE_isBuildersProfileModalEnabled,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const TriangleIconComponent = menuOpen ? TriangleUpIcon : TriangleDownIcon;

  return user ? (
    <Flyout
      show={menuOpen}
      onHide={() => setMenuOpen(false)}
      onShow={() => setMenuOpen(true)}
      placement={Flyout.PLACEMENTS.BOTTOM_END}
      renderTarget={(props) => <Tooltip.Target {...props} />}
      content={
        <Profile
          user={user}
          projectId={projectId}
          onLogout={onLogout}
          onClose={() => setMenuOpen(false)}
          UNSAFE_showBuildersUserProfileModal={
            UNSAFE_showBuildersUserProfileModal
          }
          UNSAFE_isBuildersProfileModalEnabled={
            UNSAFE_isBuildersProfileModalEnabled
          }
          onOpenSubscriptions={() => setModalOpen(true)}
          showSubscriptionsModal={showSubscriptionsModal}
        />
      }
      offset={20}
      renderBody={(props) => <Tooltip.Body {...props} padding={0} />}
      getTargetProps={() => ({
        style: { display: 'flex', alignSelf: 'center' },
      })}
    >
      <BasicButton
        onClick={() => {
          setMenuOpen(!menuOpen);
          analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_CLICK);
        }}
        data-testid="TopNavigation__user"
        style={{
          margin: 0,
          padding: 0,
          color: theme.colors.charcoal900,
          '&:hover': {
            color: theme.colors.charcoal700,
          },
        }}
      >
        <Avatar
          image={user.imageUrl}
          size={Avatar.SIZES.MEDIUM_24}
          name={user.name ? user.name : undefined}
        />
        <TriangleIconComponent
          style={{
            color: theme.colors.charcoal700,
            flexShrink: 0,
            marginLeft: 4,
          }}
          size={20}
        />
      </BasicButton>
      {showSubscriptionsModal && isModalOpen && (
        <SubscriptionsModal onCloseModal={() => setModalOpen(false)} />
      )}
    </Flyout>
  ) : (
    <ProfileLoader />
  );
};

ProfileButton.displayName = 'ProfileButton';

const ProfileBtn = ProfileButton;

// Wrap with suspense
const SuspendedProfileButton = WithAlloySuspense(ProfileBtn);

// Manually copy static properties
SuspendedProfileButton.propTypes = ProfileBtn.propTypes;

export default SuspendedProfileButton;
